import styles from './JourneyTileCard.module.scss';
import { CandidateJourneyStageStatus } from 'generated/graphql';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@spotted-zebra-uk/ui-components';
import { Link } from 'react-router-dom';
import { JourneyTile } from '../CandidatePortal.helpers';

export type TJourneyTileCardProps = {
  tile: JourneyTile;
  /** When set (up-next tiles), the whole card links into the stage flow. */
  link?: string;
};

/** One stage tile in the portal's application-journey grid. */
const JourneyTileCard: FC<TJourneyTileCardProps> = ({ tile, link }) => {
  const { t } = useTranslation();

  const chipLabel: ReactNode = {
    [CandidateJourneyStageStatus.Completed]: (
      <>
        <Icon icon="check" /> {t('candidatePortal.completed')}
      </>
    ),
    [CandidateJourneyStageStatus.Unlocked]: t('candidatePortal.upNext'),
    [CandidateJourneyStageStatus.Locked]: (
      <>
        <Icon icon="lock" /> {t('candidatePortal.locked')}
      </>
    ),
    [CandidateJourneyStageStatus.Unsuccessful]: (
      <>
        <Icon icon="close" /> {t('candidatePortal.unsuccessful')}
      </>
    ),
  }[tile.status];
  const isLocked = tile.status === CandidateJourneyStageStatus.Locked;
  const isUnsuccessful =
    tile.status === CandidateJourneyStageStatus.Unsuccessful;

  const content: ReactNode = (
    <>
      <div className={styles.thumbnail}>
        {tile.thumbnailUrl && <img src={tile.thumbnailUrl} alt="" />}
        <span
          className={`${styles.statusChip} ${
            tile.status === CandidateJourneyStageStatus.Unlocked
              ? styles.statusChipUpNext
              : ''
          } ${isUnsuccessful ? styles.statusChipFailed : ''}`}
        >
          {chipLabel}
        </span>
      </div>
      <div className={styles.body}>
        <h3 className={styles.title}>{tile.title}</h3>
        {tile.description && (
          <p className={styles.description}>{tile.description}</p>
        )}
        {tile.estimatedTimeMinutes != null && (
          <p className={styles.estimatedTime}>
            <Icon icon="clock" />{' '}
            {t('candidatePortal.estimatedTime', {
              minutes: tile.estimatedTimeMinutes,
            })}
          </p>
        )}
      </div>
      {(isLocked || isUnsuccessful || link) && (
        <div className={styles.footer}>
          {isLocked ? (
            <span className={styles.lockedHint}>
              {t('candidatePortal.lockedHint')}
            </span>
          ) : isUnsuccessful ? (
            <span className={styles.failedHint}>
              {t('candidatePortal.unsuccessfulTileHint')}
            </span>
          ) : (
            <span className={styles.continue}>
              {t('candidatePortal.continue')} →
            </span>
          )}
        </div>
      )}
    </>
  );

  return link ? (
    <Link to={link} className={`${styles.tile} ${styles.tileUpNext}`}>
      {content}
    </Link>
  ) : (
    <div
      className={`${styles.tile} ${isLocked ? styles.tileLocked : ''} ${
        isUnsuccessful ? styles.tileFailed : ''
      }`}
    >
      {content}
    </div>
  );
};

export default JourneyTileCard;
